"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomAttributesSection = void 0;
const ui_1 = require("@app/component/ui");
const form_grid_layout_1 = require("@app/component/ui/forms/form-grid-layout");
const time_input_1 = require("@app/component/ui/input/time-input/time-input");
const graphql_1 = require("@app/gql-types/graphql");
const util_1 = require("@app/util");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const custom_attribute_debounced_inputs_1 = require("../components/custom-attribute-debounced-inputs");
const shift_modal_section_1 = require("../components/shift-modal-section");
const shift_context_1 = require("../shift-context");
const use_available_shift_sections_1 = require("../use-available-shift-sections");
const generateDefaultCustomAttribute = (attribute) => {
    let defaultAttributeValue;
    switch (attribute.type) {
        case graphql_1.CustomAttributeValueType.Boolean:
            defaultAttributeValue = true;
            break;
        case graphql_1.CustomAttributeValueType.Date:
            defaultAttributeValue = (0, moment_1.default)();
            break;
        case graphql_1.CustomAttributeValueType.Multiselect:
            defaultAttributeValue = [];
            break;
        case graphql_1.CustomAttributeValueType.Select:
            defaultAttributeValue = null;
            break;
        case graphql_1.CustomAttributeValueType.Number:
            defaultAttributeValue = 0;
            break;
        case graphql_1.CustomAttributeValueType.String:
            defaultAttributeValue = '';
            break;
        case graphql_1.CustomAttributeValueType.Duration:
            defaultAttributeValue = 0;
            break;
        default:
            defaultAttributeValue = undefined;
            break;
    }
    return {
        attributeId: attribute.id,
        value: defaultAttributeValue,
    };
};
const stripRHFId = ({ customAttributeFormId, ...field }) => field;
const hasIdField = (t) => (0, util_1.isValue)(t.id);
const updateFieldValue = (field, v) => stripRHFId(Object.assign(field, { value: v }));
const fieldIsXType = (type) => (attribute, f) => (0, util_1.isDefined)(f) && attribute.type === type;
const fieldIsStringType = fieldIsXType(graphql_1.CustomAttributeValueType.String);
const fieldIsNumberType = fieldIsXType(graphql_1.CustomAttributeValueType.Number);
const fieldIsDateType = fieldIsXType(graphql_1.CustomAttributeValueType.Date);
const fieldIsSelectType = fieldIsXType(graphql_1.CustomAttributeValueType.Select);
const fieldIsMultiSelectType = fieldIsXType(graphql_1.CustomAttributeValueType.Multiselect);
const fieldIsDurationType = fieldIsXType(graphql_1.CustomAttributeValueType.Duration);
const CustomAttributeInput = ({ index, update, field, attribute }) => {
    const { disabledEditation } = (0, shift_context_1.useShift)();
    if (fieldIsStringType(attribute, field)) {
        return (react_1.default.createElement(custom_attribute_debounced_inputs_1.CustomAttributeDebouncedTextInput, { field: field, update: (v) => update(index, updateFieldValue(field, v)), disabled: (0, util_1.isDefined)(disabledEditation) }));
    }
    if (fieldIsNumberType(attribute, field)) {
        return (react_1.default.createElement(custom_attribute_debounced_inputs_1.CustomAttributeDebouncedNumberInput, { field: field, update: (v) => update(index, updateFieldValue(field, v)), disabled: (0, util_1.isDefined)(disabledEditation) }));
    }
    if (fieldIsDateType(attribute, field)) {
        return (react_1.default.createElement(ui_1.Input, { size: 'full-width', type: 'date', disabled: (0, util_1.isDefined)(disabledEditation), value: field.value ? (0, moment_1.default)(field.value).format('YYYY-MM-DD') : undefined, onChange: (v) => update(index, updateFieldValue(field, (0, moment_1.default)(v))) }));
    }
    if (fieldIsSelectType(attribute, field)) {
        const selectOptions = attribute.options?.values?.map((v) => ({
            value: v,
            label: v,
        })) ?? [];
        return (react_1.default.createElement(ui_1.Dropdown, { size: 'full-width', singleSelect: true, type: 'variable', options: selectOptions, disabled: (0, util_1.isDefined)(disabledEditation), value: selectOptions.find((opt) => opt.value === field.value), onChange: (v) => update(index, updateFieldValue(field, v.value)), hasError: (0, util_1.isNull)(field.value), errorMessage: (0, util_1.isNull)(field.value)
                ? ((0, util_1.t)('FORM_ERR_MISSING_REQUIRED') ?? 'FORM_ERR_MISSING_REQUIRED')
                : undefined }));
    }
    if (fieldIsMultiSelectType(attribute, field)) {
        const selectOptions = attribute.options?.values?.map((v) => ({
            value: v,
            label: v,
        })) ?? [];
        return (react_1.default.createElement(ui_1.Dropdown, { size: 'full-width', type: 'variable', options: selectOptions, disabled: (0, util_1.isDefined)(disabledEditation), value: selectOptions.filter((opt) => field.value.some((v) => opt.value === v)), onChange: (v) => update(index, updateFieldValue(field, v.map((v) => v.value))) }));
    }
    if (fieldIsDurationType(attribute, field)) {
        return (react_1.default.createElement(time_input_1.TimeInput, { maxHours: 25, disabled: (0, util_1.isDefined)(disabledEditation), onChange: (value) => {
                update(index, updateFieldValue(field, value || 0));
            }, value: field.value, error: typeof field.value !== 'number'
                ? (0, util_1.t)('FORM_ERR_MISSING_REQUIRED') || ''
                : undefined }));
    }
    return null;
};
const CustomAttributesSection = () => {
    const { formControl, toggledSections } = (0, shift_context_1.useShift)();
    const sections = (0, use_available_shift_sections_1.useAvailableShiftSections)();
    const filteredCustomSections = sections.filter((sec) => toggledSections.some((_sec) => sec.id === _sec) &&
        sec.isCustomAttribute === true);
    const { fields, update, replace } = (0, react_hook_form_1.useFieldArray)({
        control: formControl,
        name: 'customAttributes',
        keyName: 'customAttributeFormId',
        rules: {
            validate: (field) => {
                const hasErrors = field?.some((f) => (0, util_1.isNull)(f.value));
                return hasErrors
                    ? ((0, util_1.t)('FORM_ERR_MISSING_REQUIRED') ?? 'FORM_ERR_MISSING_REQUIRED')
                    : undefined;
            },
        },
    });
    react_1.default.useEffect(() => {
        const newFields = filteredCustomSections
            .map(({ attribute, id }) => {
            const existingField = fields.find((field) => field.attributeId === id);
            if (existingField) {
                return stripRHFId(existingField);
            }
            if (attribute && hasIdField(attribute)) {
                return generateDefaultCustomAttribute(attribute);
            }
            return null;
        })
            .filter(util_1.isValue);
        replace(newFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredCustomSections.map((sec) => sec.id).join(',')]);
    const fieldsWithAttributeData = fields
        .map((f) => {
        const attribute = filteredCustomSections.find((section) => section.id === f.attributeId)?.attribute;
        if ((0, util_1.isUndefined)(attribute)) {
            return undefined;
        }
        return {
            field: f,
            attribute,
        };
    })
        .filter(util_1.isDefined);
    return (react_1.default.createElement(react_1.default.Fragment, null, fieldsWithAttributeData.map((section, index) => (react_1.default.createElement(shift_modal_section_1.ShiftModalSection, { key: section.field.attributeId, id: section.field.attributeId, 
        // @ts-expect-error
        sectionNameSuffix: section.attribute.type === graphql_1.CustomAttributeValueType.Boolean
            ? section.field.value === true
                ? (0, util_1.t)('YES')
                : (0, util_1.t)('NO')
            : undefined },
        react_1.default.createElement(form_grid_layout_1.FormGridLayout, null,
            react_1.default.createElement(CustomAttributeInput, { ...section, index: index, update: update })))))));
};
exports.CustomAttributesSection = CustomAttributesSection;
